/**
 * OptimizationDetail — renders full detail for a single grid-search or walk-forward run.
 *
 * Grid Search:
 *   - Common header (strategy, symbol, timeframe, date range, combos, optimizedAt)
 *   - Best params + metrics card
 *   - Top-10 combinations table (Sharpe shown as number; each row clickable → reruns backtest)
 *
 * Walk-Forward:
 *   - Common header
 *   - Best params + metrics card
 *   - Pass/Fail verdict card
 *   - Per-window bar chart (train vs test Sharpe)
 *   - Windows table
 */

import { useCallback, useState } from 'react';
import type { OptimizationDetail as OptimizationDetailType, GridSearchResult, WalkForwardWindow } from '../../types/optimizations.js';
import { StrategyParameters } from '../StrategyParameters/StrategyParameters.js';
import { useClientBacktest } from '../../hooks/useClientBacktest.js';
import { useClientAggregationBacktest } from '../../hooks/useClientAggregationBacktest.js';
import { usePaperTradingStore } from '../../stores/paperTradingStore.js';
import { useConfigurationStore } from '../../stores/configurationStore.js';
import { usePortfoliosStore } from '../../stores/portfoliosStore.js';
import { getUserStrategies, getAggregation } from '../../api/client.js';
import type { Timeframe } from '../../types.js';
import { executableStrategyCode } from '../../utils/executable-strategy-code.js';

// ── helpers ────────────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 2): string {
  return n.toFixed(decimals);
}

function fmtDate(d: number | undefined): string {
  if (d == null) return '—';
  return new Date(d).toISOString().slice(0, 10);
}

function fmtMs(ms: number): string {
  return new Date(ms).toLocaleString();
}

/**
 * Returns the swept params — those with more than 1 distinct value across
 * the grid search results. Derived from allResults so it works with the
 * real API payload (which does not send paramRanges).
 */
export function getSweptParams(results: GridSearchResult[]): string[] {
  if (results.length === 0) return [];

  // Collect distinct values per param key across all results
  const distinctValues = new Map<string, Set<unknown>>();
  for (const result of results) {
    for (const [key, value] of Object.entries(result.params)) {
      let set = distinctValues.get(key);
      if (!set) {
        set = new Set<unknown>();
        distinctValues.set(key, set);
      }
      set.add(value);
    }
  }

  // A param is "swept" if it has more than 1 distinct value
  const swept: string[] = [];
  for (const [key, values] of distinctValues) {
    if (values.size > 1) swept.push(key);
  }
  return swept;
}

/**
 * Determine whether this optimization run is for an aggregation.
 *
 * Primary discriminator (migration 065): when `ownerType` is present, use it
 * exclusively — `'aggregation'` means portfolio run, `'strategy'` means
 * single-asset run, regardless of symbol or strategyName.
 *
 * Legacy fallback (pre-065 rows that have no ownerType): fall back to the
 * original heuristics — symbol === 'MULTI' or the 'aggregation-grid-search:'
 * strategyName prefix.
 */
function isAggregationRun(detail: OptimizationDetailType): boolean {
  if (detail.ownerType != null) {
    return detail.ownerType === 'aggregation';
  }
  // Legacy fallback for rows that pre-date migration 065.
  return (
    detail.symbol === 'MULTI' ||
    detail.strategyName.startsWith('aggregation-grid-search:')
  );
}

/**
 * Extract aggregation config ID embedded in strategyName when applicable.
 * Format: "aggregation-grid-search:<configId>"
 */
function extractAggConfigId(strategyName: string): string | null {
  const prefix = 'aggregation-grid-search:';
  if (!strategyName.startsWith(prefix)) return null;
  return strategyName.slice(prefix.length) || null;
}

// ── sub-components ─────────────────────────────────────────────────────────────

interface MetricsCardProps {
  label: string;
  sharpe: number;
  ret: number;
  dd: number;
  trades: number;
  winRate: number;
  pf: number;
  cagr?: number;
  /**
   * SHA-28: +- intrabar-fill-uncertainty band (percentage points). Reporting
   * metadata only — displayed alongside `ret`, never used to adjust it.
   */
  slippageBand?: number;
}

function MetricsCard({ label, sharpe, ret, dd, trades, winRate, pf, cagr, slippageBand }: MetricsCardProps) {
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{label}</h3>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs text-gray-500">Sharpe</div>
          <div className={`text-lg font-bold ${sharpe >= 1 ? 'text-green-400' : sharpe >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
            {fmt(sharpe)}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Return</div>
          <div className={`text-lg font-bold ${ret >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {fmt(ret)}%
          </div>
          {slippageBand != null && slippageBand > 0 && (
            <div className="text-[10px] text-gray-500">±{fmt(slippageBand)}% slippage band</div>
          )}
        </div>
        {cagr != null ? (
          <div>
            <div className="text-xs text-gray-500">CAGR</div>
            <div className={`text-lg font-bold ${cagr >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {fmt(cagr)}%
            </div>
          </div>
        ) : (
          <div>
            <div className="text-xs text-gray-500">Max DD</div>
            <div className="text-lg font-bold text-red-400">{fmt(dd)}%</div>
          </div>
        )}
        <div>
          <div className="text-xs text-gray-500">Trades</div>
          <div className="text-base font-semibold text-gray-200">{trades}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Win Rate</div>
          <div className="text-base font-semibold text-gray-200">{fmt(winRate)}%</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{cagr != null ? 'Max DD' : 'Profit Factor'}</div>
          <div className={`text-base font-semibold ${cagr != null ? 'text-red-400' : 'text-gray-200'}`}>
            {cagr != null ? `${fmt(dd)}%` : fmt(pf)}
          </div>
        </div>
        {cagr != null && (
          <div>
            <div className="text-xs text-gray-500">Profit Factor</div>
            <div className="text-base font-semibold text-gray-200">{fmt(pf)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Grid Search sub-components ─────────────────────────────────────────────────

interface TopCombosTableProps {
  results: GridSearchResult[];
  onRerun: (params: Record<string, unknown>) => void;
  rerunning: boolean;
}

/**
 * Picks only the specified keys from a params object.
 * Used to extract swept-only params for compact display in the top-combos table.
 */
function pickKeys(params: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      result[key] = params[key];
    }
  }
  return result;
}

function TopCombosTable({ results, onRerun, rerunning }: TopCombosTableProps) {
  const sorted = [...results].sort((a, b) => b.metrics.sharpeRatio - a.metrics.sharpeRatio).slice(0, 10);

  // Compute which params vary across all combinations — show only those in each row.
  // If no params are swept (e.g. single combo), fall back to showing all params.
  const sweptKeys = getSweptParams(results);

  // Show CAGR column only when at least one result has it (new runs).
  const hasCagr = results.some((r) => r.metrics.cagrPercent != null);

  if (sorted.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Top Combinations</h3>
        <p className="text-sm text-gray-500">No combination results recorded.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 overflow-x-auto">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Top Combinations</h3>
        <span className="text-xs text-gray-600">Click a row to rerun with those params</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-700 bg-gray-800/50">
            <th className="pb-2 pt-1 px-3">Rank</th>
            <th className="pb-2 pt-1 px-3">Params</th>
            <th className="pb-2 pt-1 px-3">Sharpe</th>
            <th className="pb-2 pt-1 px-3">Return %</th>
            {hasCagr && <th className="pb-2 pt-1 px-3">CAGR %</th>}
            <th className="pb-2 pt-1 px-3">Max DD %</th>
            <th className="pb-2 pt-1 px-3">Trades</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const sharpe = r.metrics.sharpeRatio;
            const sharpeClass = sharpe >= 1
              ? 'text-green-400'
              : sharpe >= 0
              ? 'text-yellow-400'
              : 'text-red-400';
            // Show only swept params (those that vary); fall back to all params when
            // none are swept (e.g. a single-combination result) so the cell is never empty.
            const displayParams = sweptKeys.length > 0 ? pickKeys(r.params, sweptKeys) : r.params;
            return (
              <tr
                key={i}
                onClick={() => !rerunning && onRerun(r.params)}
                className={[
                  'border-b border-gray-700/50 transition-colors',
                  rerunning
                    ? 'cursor-wait opacity-60'
                    : 'hover:bg-gray-700/40 cursor-pointer',
                ].join(' ')}
              >
                <td className="py-2.5 px-3 font-mono text-gray-300">#{i + 1}</td>
                <td className="py-2.5 px-3">
                  <StrategyParameters
                    params={displayParams}
                    layout="compact"
                    className="bg-transparent border-0 p-0"
                    emptyMessage="—"
                  />
                </td>
                <td className={`py-2.5 px-3 font-semibold tabular-nums ${sharpeClass}`}>
                  {fmt(sharpe)}
                </td>
                <td className={`py-2.5 px-3 ${r.metrics.totalReturnPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {fmt(r.metrics.totalReturnPercent)}%
                </td>
                {hasCagr && (
                  <td className={`py-2.5 px-3 ${(r.metrics.cagrPercent ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {r.metrics.cagrPercent != null ? `${fmt(r.metrics.cagrPercent)}%` : '—'}
                  </td>
                )}
                <td className="py-2.5 px-3 text-red-400">{fmt(r.metrics.maxDrawdownPercent)}%</td>
                <td className="py-2.5 px-3 text-gray-300">{r.metrics.totalTrades}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Walk-Forward sub-components ────────────────────────────────────────────────

/**
 * Format an optional numeric value as a percentage string (e.g. "28.4%"),
 * or return "—" when the value is null / undefined.
 */
function fmtOptPct(v: number | null | undefined, decimals = 1): string {
  if (v == null) return '—';
  return `${v.toFixed(decimals)}%`;
}

/**
 * Format an optional numeric value to a fixed number of decimals,
 * or return "—" when the value is null / undefined.
 */
function fmtOpt(v: number | null | undefined, decimals = 2): string {
  if (v == null) return '—';
  return v.toFixed(decimals);
}

interface VerdictCardProps {
  windows: WalkForwardWindow[];
  /** Canonical verdict driven by median OOS degradation (WF-unify PR-2). */
  passed?: boolean;
  /**
   * Median OOS degradation (primary — drives `passed`).
   * Absent on saved runs that pre-date WF-unify PR-2 persistence.
   */
  medianOosDegradation?: number | null;
  /**
   * Mean OOS degradation (legacy, kept as secondary reference).
   * Computed locally from windows when not supplied via the canonical field.
   */
  sharpeDegradation?: number | null;
  contributingWindows?: number | null;
  excludedWindows?: number | null;
}

function VerdictCard({
  windows,
  passed: passedProp,
  medianOosDegradation,
  sharpeDegradation,
  contributingWindows,
  excludedWindows,
}: VerdictCardProps) {
  // Compute legacy mean degradation + test Sharpe from windows when
  // the canonical fields are absent (backward compat for old saved runs).
  const avgDegrade =
    windows.length > 0
      ? windows.reduce((s, w) => s + (w.oosDegrade ?? 0), 0) / windows.length
      : 0;
  const avgTestSharpe =
    windows.length > 0
      ? windows.reduce((s, w) => s + w.testMetrics.sharpeRatio, 0) / windows.length
      : 0;

  // Use the server-supplied verdict when available; fall back to legacy heuristic.
  const passed = passedProp ?? (avgDegrade < 30 && avgTestSharpe > 0);

  // The mean degradation to show as secondary: prefer the supplied `sharpeDegradation`
  // (the persisted mean), otherwise use the locally computed avgDegrade.
  const meanDegradationDisplay: number | null =
    sharpeDegradation != null ? sharpeDegradation : windows.length > 0 ? avgDegrade : null;

  const hasCanonical = medianOosDegradation != null;

  return (
    <div
      data-testid="wf-verdict"
      className={`rounded-lg border p-4 ${
        passed
          ? 'bg-green-900/30 border-green-700'
          : 'bg-red-900/30 border-red-700'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`text-2xl font-bold shrink-0 ${passed ? 'text-green-400' : 'text-red-400'}`}
          data-testid="wf-verdict-label"
        >
          {passed ? 'PASS' : 'FAIL'}
        </div>
        <div className="text-sm text-gray-300 space-y-0.5">
          {/* Primary: median degradation (canonical) */}
          <div>
            <span className="text-gray-400">OOS degradation (median):</span>{' '}
            <span className="font-semibold" data-testid="wf-median-degradation">
              {hasCanonical ? `${medianOosDegradation!.toFixed(1)}%` : '—'}
            </span>
          </div>
          {/* Secondary: mean degradation */}
          <div className="text-xs text-gray-500">
            mean:{' '}
            <span data-testid="wf-mean-degradation">
              {meanDegradationDisplay != null ? `${meanDegradationDisplay.toFixed(1)}%` : '—'}
            </span>
            {hasCanonical && meanDegradationDisplay != null && (
              <span className="ml-1 text-gray-600">(verdict uses median)</span>
            )}
          </div>
          {/* Contributing / excluded windows */}
          {(contributingWindows != null || excludedWindows != null) && (
            <div className="text-xs text-gray-500" data-testid="wf-window-counts">
              {contributingWindows != null ? contributingWindows : '—'} contributing,{' '}
              {excludedWindows != null ? excludedWindows : '—'} excluded
            </div>
          )}
          {/* Legacy: avg test Sharpe (shown when canonical fields absent) */}
          {!hasCanonical && (
            <div>Avg test Sharpe: <span className="font-semibold">{fmt(avgTestSharpe)}</span> (must be &gt; 0)</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── OOS Headline Block ─────────────────────────────────────────────────────────

interface OosHeadlineBlockProps {
  oosReturnPercent?: number | null;
  oosSharpe?: number | null;
  oosMaxDrawdownPercent?: number | null;
}

/**
 * Shows the stitched OOS equity headline metrics: return %, Sharpe, max DD.
 * Only rendered when at least one field is non-null.
 * Guarded with `!= null` — older saved runs won't have these.
 */
function OosHeadlineBlock({ oosReturnPercent, oosSharpe, oosMaxDrawdownPercent }: OosHeadlineBlockProps) {
  // Don't render at all when none of the fields have actual numeric values.
  // Guard against both undefined (field absent) and null (field present but no data).
  const anyPresent =
    oosReturnPercent != null ||
    oosSharpe != null ||
    oosMaxDrawdownPercent != null;
  if (!anyPresent) return null;

  return (
    <div
      className="bg-gray-800 rounded-lg border border-gray-700 p-4"
      data-testid="wf-oos-headline"
    >
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
        Stitched OOS Performance
      </h3>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs text-gray-500">OOS Return</div>
          <div
            className={`text-lg font-bold ${(oosReturnPercent ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}
            data-testid="wf-oos-return"
          >
            {fmtOptPct(oosReturnPercent)}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">OOS Sharpe</div>
          <div
            className={`text-lg font-bold ${
              oosSharpe == null ? 'text-gray-400' : oosSharpe >= 1 ? 'text-green-400' : oosSharpe >= 0 ? 'text-yellow-400' : 'text-red-400'
            }`}
            data-testid="wf-oos-sharpe"
          >
            {fmtOpt(oosSharpe)}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">OOS Max DD</div>
          <div
            className="text-lg font-bold text-red-400"
            data-testid="wf-oos-maxdd"
          >
            {fmtOptPct(oosMaxDrawdownPercent)}
          </div>
        </div>
      </div>
    </div>
  );
}

interface WindowBarChartProps {
  windows: WalkForwardWindow[];
}

function WindowBarChart({ windows }: WindowBarChartProps) {
  const allSharpes = windows.flatMap(w => [w.trainMetrics.sharpeRatio, w.testMetrics.sharpeRatio]);
  const maxS = Math.max(...allSharpes, 0.1);

  const BAR_W = 24;
  const GAP = 8;
  const PAIR_GAP = 20;
  const H = 160;
  const PAD_TOP = 16;
  const PAD_LEFT = 40;
  const PAD_BOTTOM = 36;
  const plotH = H - PAD_TOP - PAD_BOTTOM;

  const pairW = BAR_W * 2 + GAP;
  const totalW = PAD_LEFT + windows.length * (pairW + PAIR_GAP) + 16;

  function barHeight(sharpe: number): number {
    if (sharpe <= 0) return 0;
    return Math.min((sharpe / maxS) * plotH, plotH);
  }

  function barY(sharpe: number): number {
    return PAD_TOP + plotH - barHeight(sharpe);
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 overflow-x-auto" data-testid="wf-window-chart">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
        Train vs Test Sharpe per Window
      </h3>
      <div className="overflow-x-auto">
        <svg width={Math.max(totalW, 280)} height={H} className="overflow-visible">
          {/* Baseline */}
          <line
            x1={PAD_LEFT}
            x2={totalW - 16}
            y1={PAD_TOP + plotH}
            y2={PAD_TOP + plotH}
            stroke="#6b7280"
          />
          {/* Y axis */}
          <line x1={PAD_LEFT} x2={PAD_LEFT} y1={PAD_TOP} y2={PAD_TOP + plotH} stroke="#6b7280" />
          {/* Y ticks — tick marks only, no text labels */}
          {[0, 0.25, 0.5, 0.75, 1.0].map(t => {
            const y = PAD_TOP + plotH - t * plotH;
            return <line key={t} x1={PAD_LEFT - 3} x2={PAD_LEFT} y1={y} y2={y} stroke="#6b7280" />;
          })}
          {/* Bars */}
          {windows.map((w, i) => {
            const x = PAD_LEFT + i * (pairW + PAIR_GAP);
            const trainH = barHeight(w.trainMetrics.sharpeRatio);
            const testH = barHeight(w.testMetrics.sharpeRatio);
            return (
              <g key={i}>
                {/* Train bar */}
                <rect
                  x={x}
                  y={barY(w.trainMetrics.sharpeRatio)}
                  width={BAR_W}
                  height={trainH}
                  fill="#60a5fa"
                  fillOpacity={0.8}
                  rx={2}
                  aria-label={`W${i + 1} Train Sharpe: ${fmt(w.trainMetrics.sharpeRatio)}`}
                />
                {/* Test bar */}
                <rect
                  x={x + BAR_W + GAP}
                  y={barY(w.testMetrics.sharpeRatio)}
                  width={BAR_W}
                  height={testH}
                  fill="#34d399"
                  fillOpacity={0.8}
                  rx={2}
                  aria-label={`W${i + 1} Test Sharpe: ${fmt(w.testMetrics.sharpeRatio)}`}
                />
                {/* Window label */}
                <text
                  x={x + pairW / 2}
                  y={PAD_TOP + plotH + 14}
                  textAnchor="middle"
                  fontSize={9}
                  fill="#9ca3af"
                >
                  W{i + 1}
                </text>
              </g>
            );
          })}
          {/* Legend */}
          <g>
            <rect x={PAD_LEFT} y={H - 10} width={10} height={8} fill="#60a5fa" rx={1} />
            <text x={PAD_LEFT + 13} y={H - 3} fontSize={9} fill="#9ca3af">Train</text>
            <rect x={PAD_LEFT + 50} y={H - 10} width={10} height={8} fill="#34d399" rx={1} />
            <text x={PAD_LEFT + 63} y={H - 3} fontSize={9} fill="#9ca3af">Test</text>
          </g>
        </svg>
      </div>
    </div>
  );
}

interface WindowsTableProps {
  windows: WalkForwardWindow[];
}

function WindowsTable({ windows }: WindowsTableProps) {
  if (windows.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <p className="text-sm text-gray-500">No windows recorded.</p>
      </div>
    );
  }

  // Show CAGR columns only when at least one window has it in train or test metrics.
  const hasCagr = windows.some(
    (w) => w.trainMetrics.cagrPercent != null || w.testMetrics.cagrPercent != null,
  );

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 overflow-x-auto">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Windows</h3>
      <table className="w-full text-sm whitespace-nowrap">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-700 bg-gray-800/50">
            <th className="pb-2 pt-1 px-3">Train Period</th>
            <th className="pb-2 pt-1 px-3">Test Period</th>
            <th className="pb-2 pt-1 px-3">Best Params</th>
            <th className="pb-2 pt-1 px-3">Train Sharpe</th>
            {hasCagr && <th className="pb-2 pt-1 px-3">Train CAGR</th>}
            <th className="pb-2 pt-1 px-3">Test Sharpe</th>
            {hasCagr && <th className="pb-2 pt-1 px-3">Test CAGR</th>}
            <th className="pb-2 pt-1 px-3">Degradation</th>
          </tr>
        </thead>
        <tbody>
          {windows.map((w, i) => (
            <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
              <td className="py-2.5 px-3 text-gray-300">
                {fmtDate(w.trainStart)} – {fmtDate(w.trainEnd)}
              </td>
              <td className="py-2.5 px-3 text-gray-300">
                {fmtDate(w.testStart)} – {fmtDate(w.testEnd)}
              </td>
              <td className="py-2.5 px-3">
                <StrategyParameters
                  params={w.bestTrainParams}
                  layout="compact"
                  className="bg-transparent border-0 p-0"
                  emptyMessage="—"
                />
              </td>
              <td className="py-2.5 px-3 font-semibold text-blue-400">
                {fmt(w.trainMetrics.sharpeRatio)}
              </td>
              {hasCagr && (
                <td className={`py-2.5 px-3 font-semibold ${(w.trainMetrics.cagrPercent ?? 0) >= 0 ? 'text-blue-300' : 'text-red-400'}`}>
                  {w.trainMetrics.cagrPercent != null ? `${fmt(w.trainMetrics.cagrPercent)}%` : '—'}
                </td>
              )}
              <td className={`py-2.5 px-3 font-semibold ${w.testMetrics.sharpeRatio > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {fmt(w.testMetrics.sharpeRatio)}
              </td>
              {hasCagr && (
                <td className={`py-2.5 px-3 font-semibold ${(w.testMetrics.cagrPercent ?? 0) >= 0 ? 'text-green-300' : 'text-red-400'}`}>
                  {w.testMetrics.cagrPercent != null ? `${fmt(w.testMetrics.cagrPercent)}%` : '—'}
                </td>
              )}
              <td className={`py-2.5 px-3 font-semibold ${(w.oosDegrade ?? 0) < 30 ? 'text-yellow-400' : 'text-red-400'}`}>
                {(w.oosDegrade ?? 0).toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Common header ──────────────────────────────────────────────────────────────

interface CommonHeaderProps {
  detail: OptimizationDetailType;
  onBack: () => void;
  onNavigateToOwner?: () => void;
}

function CommonHeader({ detail, onBack, onNavigateToOwner }: CommonHeaderProps) {
  const ownerLabel = detail.ownerName
    ? `${detail.ownerType === 'strategy' ? 'Strategy' : 'Portfolio'}: ${detail.ownerName}`
    : null;

  return (
    <div className="flex items-start gap-3 mb-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors mt-0.5 shrink-0"
        aria-label="Back to list"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <h2 className="text-lg font-bold text-white">{detail.strategyName}</h2>
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-900/50 text-blue-300 border border-blue-700">
            {detail.type === 'grid_search' ? 'Grid Search' : 'Walk-Forward'}
          </span>
          {/* Migration 065: owner back-link */}
          {ownerLabel && onNavigateToOwner && (
            <button
              onClick={onNavigateToOwner}
              className="text-[11px] text-blue-400 hover:text-blue-300 hover:underline leading-none"
              title={`Navigate to owning ${detail.ownerType}`}
            >
              {ownerLabel} ↗
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
          <span>{detail.symbol}</span>
          <span>{detail.timeframe}</span>
          <span>{fmtDate(detail.startDate)} – {fmtDate(detail.endDate)}</span>
          <span>{detail.testedCombinations}/{detail.totalCombinations} combos</span>
          <span>{fmtMs(detail.optimizedAt)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────────

export interface OptimizationDetailProps {
  detail: OptimizationDetailType;
  onBack: () => void;
}

export function OptimizationDetail({ detail, onBack }: OptimizationDetailProps) {
  const isGS = detail.type === 'grid_search';
  const allResults = detail.allResults ?? [];
  const windows = detail.windows ?? [];

  const { run: runBacktest, running: singleRunning } = useClientBacktest();
  const { run: runAggregationBacktest, running: aggRunning } = useClientAggregationBacktest();
  const backtestRunning = singleRunning || aggRunning;
  const setActivePage = usePaperTradingStore((s) => s.setActivePage);
  const setSelectedStrategyId = useConfigurationStore((s) => s.setSelectedStrategyId);
  const setSelectedPortfolioId = usePortfoliosStore((s) => s.setSelectedPortfolioId);

  // Migration 065: owner back-link navigation
  const handleNavigateToOwner = useCallback(() => {
    if (detail.ownerType === 'strategy' && detail.ownerId) {
      setSelectedStrategyId(detail.ownerId);
      setActivePage('configurations');
    } else if (detail.ownerType === 'aggregation' && detail.ownerId) {
      setSelectedPortfolioId(detail.ownerId);
      setActivePage('portfolios');
    }
  }, [detail.ownerType, detail.ownerId, setSelectedStrategyId, setSelectedPortfolioId, setActivePage]);

  const [rerunError, setRerunError] = useState<string | null>(null);

  const handleComboRerun = useCallback(
    async (params: Record<string, unknown>) => {
      if (backtestRunning) return;
      setRerunError(null);

      const isAgg = isAggregationRun(detail);

      if (isAgg) {
        // Aggregation rerun: fetch config, apply combo params, run via worker.
        //
        // Migration 065: when ownerType is explicitly 'aggregation', use ownerId
        // as the definitive aggregation config ID (avoids fragile strategyName parsing).
        // Fall back to extractAggConfigId(strategyName) only for legacy pre-065 rows
        // that lack an ownerId.
        const aggConfigId =
          detail.ownerType === 'aggregation' && detail.ownerId
            ? detail.ownerId
            : extractAggConfigId(detail.strategyName);

        if (!aggConfigId) {
          setRerunError(
            'Cannot determine aggregation config ID. ' +
            'Aggregation reruns require either an ownerId (migration 065) or ' +
            'a strategyName in the format "aggregation-grid-search:<configId>".',
          );
          return;
        }

        let aggConfig: Awaited<ReturnType<typeof getAggregation>>;
        try {
          aggConfig = await getAggregation(aggConfigId);
        } catch {
          setRerunError(
            `Aggregation config "${aggConfigId}" not found. The config may have been deleted.`,
          );
          return;
        }

        const startDateStr = detail.startDate
          ? new Date(detail.startDate).toISOString().slice(0, 10)
          : '';
        const endDateStr = detail.endDate
          ? new Date(detail.endDate).toISOString().slice(0, 10)
          : '';

        if (!startDateStr || !endDateStr) {
          setRerunError('Cannot rerun: start/end dates are missing from this optimization run.');
          return;
        }

        try {
          await runAggregationBacktest({
            subStrategies: aggConfig.subStrategies.map((sub) => ({
              strategyName: sub.strategyName,
              symbol: sub.symbol,
              timeframe: sub.timeframe as Timeframe,
              params: sub.params ?? {},
              exchange: sub.exchange ?? aggConfig.exchange,
            })),
            allocationMode: aggConfig.allocationMode,
            maxPositions: aggConfig.maxPositions,
            initialCapital: aggConfig.initialCapital,
            startDate: startDateStr,
            endDate: endDateStr,
            exchange: aggConfig.exchange,
            // Combo params are applied as sharedParams — they override sub-strategy defaults.
            // Grid search typically sweeps a single global param (e.g. "period"), so this
            // merge correctly propagates the selected combo value to all sub-strategies.
            // Un-swept per-sub-strategy params are preserved via resolvedPerSubParams in the worker.
            sharedParams: params as Record<string, unknown>,
            aggregationName: aggConfig.name,
          });
          setActivePage('backtesting');
        } catch (err) {
          setRerunError(err instanceof Error ? err.message : String(err));
        }
        return;
      }

      // Single-asset: fetch strategy code and rerun via Web Worker
      let strategyCode: string | null = null;
      try {
        const strategies = await getUserStrategies();
        const match = strategies.find(
          (s) => s.name === detail.strategyName,
        );
        if (!match) {
          throw new Error(
            `Strategy "${detail.strategyName}" not found in user strategies. It may have been deleted.`,
          );
        }
        strategyCode = executableStrategyCode(match);
      } catch (err) {
        setRerunError(err instanceof Error ? err.message : String(err));
        return;
      }

      // Derive date strings from numeric ms timestamps
      const startDateStr = detail.startDate
        ? new Date(detail.startDate).toISOString().slice(0, 10)
        : '';
      const endDateStr = detail.endDate
        ? new Date(detail.endDate).toISOString().slice(0, 10)
        : '';

      if (!startDateStr || !endDateStr) {
        setRerunError('Cannot rerun: start/end dates are missing from this optimization run.');
        return;
      }

      // Resolve backtest config: use persisted config when available (new runs),
      // fall back to documented historical grid-search defaults for legacy runs saved
      // before backtestConfig was persisted (migration 054). This fallback is a
      // UI-replay convenience only — it does NOT affect core financial calculations.
      const btCfg = detail.backtestConfig ?? {
        initialCapital: 10000,
        leverage: 1,
        takerFeeRate: 0.001,
        slippagePercent: 0.05,
        mode: 'futures' as const,
        exchange: 'bybit',
      };

      try {
        await runBacktest({
          strategyCode,
          strategyName: detail.strategyName,
          symbol: detail.symbol,
          timeframe: detail.timeframe,
          params,
          startDate: startDateStr,
          endDate: endDateStr,
          initialCapital: btCfg.initialCapital,
          leverage: btCfg.leverage,
          takerFeeRate: btCfg.takerFeeRate,
          slippagePercent: btCfg.slippagePercent,
          mode: btCfg.mode,
          exchange: btCfg.exchange,
          // The grid-search worker does NOT fetch open-interest or long/short-ratio.
          // Setting these to false reproduces the same auxiliary-data profile so the
          // combo rerun matches the numbers stored in the grid-search result.
          includeOpenInterest: false,
          includeLongShortRatio: false,
        });
        // Navigate to backtesting dashboard where the result is displayed
        setActivePage('backtesting');
      } catch (err) {
        setRerunError(err instanceof Error ? err.message : String(err));
      }
    },
    [backtestRunning, detail, runBacktest, runAggregationBacktest, setActivePage],
  );

  const hasOwner = !!(detail.ownerType && detail.ownerId && detail.ownerName);

  return (
    <div className="space-y-4">
      <CommonHeader
        detail={detail}
        onBack={onBack}
        onNavigateToOwner={hasOwner ? handleNavigateToOwner : undefined}
      />

      {rerunError && (
        <div role="alert" className="rounded-lg bg-red-900/30 border border-red-700 px-4 py-3 text-sm text-red-300">
          {rerunError}
          <button
            onClick={() => setRerunError(null)}
            className="ml-3 text-red-400 hover:text-red-200 underline text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MetricsCard
          label="Best Result Metrics"
          sharpe={detail.bestMetrics.sharpeRatio}
          ret={detail.bestMetrics.totalReturnPercent}
          dd={detail.bestMetrics.maxDrawdownPercent}
          trades={detail.bestMetrics.totalTrades}
          winRate={detail.bestMetrics.winRate}
          pf={detail.bestMetrics.profitFactor}
          cagr={detail.bestMetrics.cagrPercent}
          slippageBand={detail.bestMetrics.slippageUncertaintyBandPercent}
        />
        <StrategyParameters
          params={detail.bestParams}
          title="Best Parameters"
          emptyMessage="No parameters recorded."
        />
      </div>

      {isGS && (
        <TopCombosTable
          results={allResults}
          onRerun={handleComboRerun}
          rerunning={backtestRunning}
        />
      )}

      {!isGS && (
        <>
          <VerdictCard
            windows={windows}
            passed={detail.passed}
            medianOosDegradation={detail.medianOosDegradation}
            sharpeDegradation={detail.sharpeDegradation}
            contributingWindows={detail.contributingWindows}
            excludedWindows={detail.excludedWindows}
          />
          <OosHeadlineBlock
            oosReturnPercent={detail.oosReturnPercent}
            oosSharpe={detail.oosSharpe}
            oosMaxDrawdownPercent={detail.oosMaxDrawdownPercent}
          />
          <WindowBarChart windows={windows} />
          <WindowsTable windows={windows} />
        </>
      )}
    </div>
  );
}
